import type { TFunction, i18n } from '../contexts/translation-context';
import { ErrorTypes } from '@wix/form-validator';
import { type FieldError } from './error-types';
import { TRANSLATIONS_NAMESPACE } from '../constants/locale';

const defaultErrorKeys = {
  [ErrorTypes.UNKNOWN_ERROR]: 'input.error.message.unknown-error',
  [ErrorTypes.TYPE_ERROR]: 'input.error.message.type-error',
  [ErrorTypes.REQUIRED_VALUE_ERROR]: 'input.error.message.required-error',
  [ErrorTypes.UNKNOWN_VALUE_ERROR]: 'input.error.message.unknown-value-error',
  [ErrorTypes.MAX_LENGTH_ERROR]: 'input.error.message.max-length-error',
  [ErrorTypes.MIN_LENGTH_ERROR]: 'input.error.message.min-length-error',
  [ErrorTypes.PATTERN_ERROR]: 'input.error.message.pattern-error',
  [ErrorTypes.FORMAT_ERROR]: 'input.error.message.format-error',
  [ErrorTypes.MAX_VALUE_ERROR]: 'input.error.message.max-value-error',
  [ErrorTypes.MIN_VALUE_ERROR]: 'input.error.message.min-value-error',
  [ErrorTypes.MULTIPLE_OF_VALUE_ERROR]:
    'input.error.message.multiple-of-value-error',
  [ErrorTypes.MIN_ITEMS_ERROR]: 'input.error.message.min-items-error',
  [ErrorTypes.MAX_ITEMS_ERROR]: 'input.error.message.max-items-error',
  [ErrorTypes.NOT_ALLOWED_VALUE_ERROR]: 'input.error.message.not-allowed-value',
  [ErrorTypes.FIELDS_COMPATIBILITY_ERROR]:
    'input.error.fields-compatibility-error',
  [ErrorTypes.FIELDS_COUNT_RESTRICTIONS_ERROR]:
    'input.error.fields-count-restrictions-error',
  [ErrorTypes.FORMS_COUNT_RESTRICTIONS_ERROR]:
    'input.error.forms-count-restrictions-error',
  [ErrorTypes.STEPS_COUNT_RESTRICTIONS_ERROR]:
    'input.error.steps-count-restrictions-error',
  [ErrorTypes.RULES_COUNT_RESTRICTIONS_ERROR]:
    'input.error.rules-count-restrictions-error',
  [ErrorTypes.FILE_UPLOAD_RESTRICTIONS_ERROR]:
    'input.error.file-upload-restrictions-error',
  [ErrorTypes.DISABLED_FORM_ERROR]: 'input.error.disabled-form-error',
  [ErrorTypes.INVALID_PHONE_COUNTRY_CODE_ERROR]:
    'input.error.message.invalid-phone-country-code-error',
  [ErrorTypes.INVALID_STAFF_ID_ERROR]:
    'input.error.message.invalid-staff-id-error',
  [ErrorTypes.INVALID_LOCATION_ID_ERROR]:
    'input.error.message.invalid-location-id-error',
  [ErrorTypes.MISSING_SERVICE_OPTION_ERROR]: 'TBD',
  [ErrorTypes.INVALID_SERVICE_OPTIONS_ERROR]: 'TBD',
  [ErrorTypes.NO_AVAILABLE_SERVICE_OPTIONS_ERROR]: 'TBD',
  [ErrorTypes.EXACT_CHARACTER_LENGTH_ERROR]:
    'input.error.message.exact-character-length-error',
  [ErrorTypes.CHARACTER_LENGTH_RANGE_ERROR]:
    'input.error.message.character-length-range-error',
  [ErrorTypes.VALUE_RANGE_ERROR]: 'input.error.message.value-range-error',
  [ErrorTypes.EXACT_ITEMS_NUMBER_ERROR]:
    'input.error.message.exact-items-number-error',
  [ErrorTypes.DECIMAL_POINT_ERROR]: 'input.error.message.decimal_point_error',
  [ErrorTypes.INCOMPLETE_DATE_ERROR]:
    'input.error.message.incomplete-date-error',
  [ErrorTypes.INVALID_VALUE_FOR_PATTERN_ERROR]:
    'input.error.message.invalid-value-for-pattern',
};

export const getTranslatedError = ({
  translation,
  error,
  fieldType,
  suffix,
  currency,
}: {
  translation: { t: TFunction; i18n: i18n };
  error: FieldError;
  fieldType?: string;
  suffix?: string;
  currency?: string;
}): string => {
  const { t, i18n } = translation;
  const formattedFieldType = getFormattedFieldType(fieldType ?? '');
  const errorKey = defaultErrorKeys[error.errorType];
  const params = { ...error.params, currency };
  const fieldErrorKey = `${formattedFieldType}.${errorKey}`;

  if (suffix) {
    const fieldErrorKeyWithSuffix = `${fieldErrorKey}.${suffix}`;
    const errorKeyWithSuffix = `${errorKey}.${suffix}`;
    if (i18n.exists(`${TRANSLATIONS_NAMESPACE}:${fieldErrorKeyWithSuffix}`)) {
      return t(fieldErrorKeyWithSuffix, params);
    }
    if (i18n.exists(`${TRANSLATIONS_NAMESPACE}:${errorKeyWithSuffix}`)) {
      return t(errorKeyWithSuffix, params);
    }
  }

  if (i18n.exists(`${TRANSLATIONS_NAMESPACE}:${fieldErrorKey}`)) {
    return t(fieldErrorKey, params);
  }

  return t(defaultErrorKeys[error.errorType], params);
};

const getFormattedFieldType = (string: string) =>
  string.toLowerCase().replace(/_/g, '-');
